import { useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu, Search, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import { Brand, SidebarNav } from "./Sidebar";
import { SafeModeBanner } from "./SafeModeBanner";
import { CommandSearch } from "./CommandSearch";

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user.full_name || user.email).split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-slate-100" data-testid="user-menu-trigger">
          <span className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">{initials}</span>
          <span className="hidden sm:block text-left leading-tight">
            <span className="block text-sm font-medium text-slate-800">{user.full_name}</span>
            <span className="block text-[11px] text-slate-400">{ROLE_LABELS[user.role]}</span>
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-slate-500 font-normal">{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/account")} data-testid="user-menu-account"><UserCog className="h-4 w-4 mr-2" /> My account & MFA</DropdownMenuItem>
        <DropdownMenuItem onClick={logout} data-testid="user-menu-logout"><LogOut className="h-4 w-4 mr-2" /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50">
      <SafeModeBanner />
      <div className="flex">
        <aside className="hidden lg:flex flex-col w-60 shrink-0 bg-white border-r border-slate-200 min-h-[calc(100vh-37px)] sticky top-[37px] self-start" data-testid="sidebar">
          <Link to="/dashboard"><Brand /></Link>
          <SidebarNav />
        </aside>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-64 p-0 bg-white" data-testid="mobile-nav">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <Brand />
            <SidebarNav onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex-1 min-w-0">
          <header className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6 sticky top-[37px] z-40">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
            <button onClick={() => setSearchOpen(true)} data-testid="global-search-button"
              className="flex-1 max-w-md flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-400 hover:border-slate-300 transition-colors">
              <Search className="h-4 w-4" />
              <span className="flex-1 text-left truncate">Search contractors, IDs, companies…</span>
              <kbd className="hidden sm:inline font-mono text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5">Ctrl K</kbd>
            </button>
            <div className="ml-auto"><UserMenu /></div>
          </header>
          <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px]"><Outlet /></main>
        </div>
      </div>
      <CommandSearch open={searchOpen} setOpen={setSearchOpen} />
    </div>
  );
}
